import { useLayoutEffect, useRef } from 'react'
import { ChevronUp, ChevronDown, GripVertical, Plus, Trash2 } from 'lucide-react'
import { useEvaluatedAttributes } from '@ea-lab/reactive-json'
import { useRowContext } from '../editable/useRowContext'
import { resolveRowAction, splitField } from '../editable/rowPaths'
import { getAtPath } from '../editable/pathUtils'
import { pickEditorOptions, EDITOR_ICONS, EDITOR_TITLES } from '../editable/editorOptions'
import { useLucideIcon, FALLBACK_ICON } from './lucideRegistry'

/**
 * ReactiveJSON primitive — the standard per-row rail of an inline-edited list:
 * drag to reorder, then move up / move down / insert after / remove. Rendered
 * inside a Switch row template; hidden outside edit mode (`builder-edit-only`).
 *
 *   templates:
 *     statCell:
 *       type: div
 *       attributes: { class: 'relative …' }
 *       content:
 *         - type: BuilderRowToolbar
 *           field: stats                             # dotted for a nested list: regions.destinations
 *           defaultItem: { value: '0', label: 'Nouvelle stat' }
 *           labels: { item: 'stat' }                 # optional, for the button titles
 *         - …cells…
 *
 * THE RAIL IS ALSO THE ROW'S LATERAL MENU for everything that has no text to
 * click in the render — an address, an icon, a background. Declare them with
 * `edit:` (each entry is an `editable:` value: `column:` for a cell of the
 * current row, `field:` for a whole field) instead of parking a « 🔗 URL » pill
 * beside the content:
 *
 *   - type: BuilderRowToolbar
 *     field: bottomLinks
 *     edit:
 *       - { column: url, type: link, label: 'Lien — adresse' }
 *
 * WITHOUT `field:` the same primitive is a pure edit rail — no grip, no row
 * actions — for a link that belongs to no list (a block's CTA). Drop it as the
 * first child of the element that holds the link; it shows on that element's
 * hover / focus like any other rail:
 *
 *   - type: BuilderRowToolbar
 *     edit:
 *       - { field: ctaUrl, type: link, label: 'Bouton — adresse' }
 *
 * AT REST THE BLOCK LOOKS LIKE THE PUBLIC RENDER. Nothing shows until a row is
 * hovered or focused: then a grip appears in the whitespace LEFT of the row
 * (`gutter`) or floating just above it (`above`), and the four buttons unfold
 * only once the pointer reaches the rail itself. Twenty links no longer mean a
 * hundred permanently visible controls.
 *
 * PLACEMENT is auto-detected from the row's own layout (`placement: auto`, the
 * default) because the same primitive serves both a stacked list of links and a
 * flex row of social icons: a left gutter only exists in a vertical list, so a
 * row laid out by a flex-row/grid parent gets the floating `above` rail
 * instead. Pass `placement: gutter | above | below | inline` to force it. `inline` is
 * the legacy in-the-flow rail, kept for the defs that still declare
 * `attributes: { style: { position: static } }`.
 *
 * The rail STAMPS ITS PARENT (the row root) with `data-builder-row-root` +
 * field / list / position. That marker is what lets the canvas resolve a drop
 * target during a drag, and what scopes the CSS — a child cannot style or be
 * found through its parent otherwise, and asking 34 block defs to carry the
 * marker by hand would make the pattern impossible to change again.
 *
 * Every action is ONE click, applied immediately — a removal is taken back from
 * the canvas-level "Annuler" banner, not prevented by a confirmation step (a
 * second click on a 24 px icon is the slowest possible way to delete a row).
 *
 * Each button carries the markers the runtime reads; the click is handled by
 * `BuilderBlockWrapper`, which applies the action to the array and commits it
 * whole. The grip carries the same markers so a drag (pointer) or Arrow
 * Up/Down (keyboard, on the focused grip) resolves to the same row.
 */

/**
 * One rail button opening a field editor. It carries the SAME markers a cell
 * carries (`data-builder-field` / `-row-path` / `-type` / `-options`), so
 * `BuilderBlockWrapper` resolves and commits it through the existing path —
 * the entry point moved to the rail, nothing else.
 */
const RailEditButton = ({ decl, fieldKey, rowPath }) => {
  const type = decl.type ?? decl.editorType
  const Icon = useLucideIcon(decl.icon ?? EDITOR_ICONS[type] ?? 'Settings')
  const title = decl.label ?? EDITOR_TITLES[type] ?? 'Modifier'
  const options = pickEditorOptions(decl)
  const Rendered = Icon === null ? FALLBACK_ICON : Icon

  return (
    <button
      type="button"
      className="builder-row-action"
      title={title}
      aria-label={title}
      data-action="edit"
      data-builder-field={fieldKey}
      data-builder-row-path={rowPath ?? undefined}
      data-builder-type={type}
      data-builder-options={Object.keys(options).length > 0 ? JSON.stringify(options) : undefined}
    >
      {/* Undefined = a lazy icon still loading: the button keeps its size. */}
      {Rendered ? <Rendered size={13} aria-hidden="true" /> : <span style={{ width: 13, height: 13 }} />}
    </button>
  )
}

/** Height the rail needs to clear above a row before it has to flip below. */
const RAIL_CLEARANCE = 34

/** Top of the scrollport the row lives in — the ceiling the rail must clear. */
const scrollportTop = (el) => {
  let node = el?.parentElement
  while (node && node !== document.body) {
    const overflowY = getComputedStyle(node).overflowY
    if (overflowY === 'auto' || overflowY === 'scroll') return node.getBoundingClientRect().top
    node = node.parentElement
  }
  return 0
}

/**
 * A row whose siblings sit BESIDE it has no left gutter to borrow, so its rail
 * floats clear of the row — ABOVE it by default, BELOW it when the row is too
 * close to the top of its scrollport for the rail to fit. Without that flip a
 * site header (the first block of a page, and a `sticky` one at that) put its
 * rail at a negative y: off-screen, and in the builder behind the toolbar.
 */
const detectPlacement = (rowEl) => {
  const parent = rowEl?.parentElement
  if (!parent || typeof getComputedStyle !== 'function') return 'above'
  const parentDisplay = getComputedStyle(parent).display
  const beside =
    parentDisplay === 'grid' ||
    parentDisplay === 'inline-grid' ||
    ((parentDisplay === 'flex' || parentDisplay === 'inline-flex') &&
      getComputedStyle(parent).flexDirection.startsWith('row')) ||
    // An inline row root (a bare `<a>` in a text flow) has no box to hang a
    // gutter on either.
    getComputedStyle(rowEl).display.startsWith('inline')
  if (!beside) return 'gutter'
  return rowEl.getBoundingClientRect().top - scrollportTop(rowEl) < RAIL_CLEARANCE ? 'below' : 'above'
}

export const BuilderRowToolbar = ({ props, path, datafield }) => {
  const { topField, nested, rowPath, topValue } = useRowContext(props, path, datafield)
  const evaluatedAttrs = useEvaluatedAttributes(props.attributes ?? {})
  const { className, ...restAttrs } = evaluatedAttrs
  const railRef = useRef(null)

  // Titles: either full labels (`labels: {up, down, insert, remove, toolbar}`)
  // or derived from `labels.item` in a form that carries no article (no gender
  // to get wrong: « Monter — région »).
  const item = props.labels?.item ?? 'élément'
  const label = (key, fallback) => props.labels?.[key] ?? `${fallback} — ${item}`
  const options = props.defaultItem != null ? JSON.stringify({ defaultItem: props.defaultItem }) : undefined
  const target = (action) => resolveRowAction({ action, nested, rowPath })

  // Where the row sits among its siblings: the first row gets no "up", the
  // last no "down" (rendered absent, not merely disabled).
  const rowTarget = target('remove')
  const siblings = rowTarget ? getAtPath(topValue, rowTarget.arrayPath) : null
  const count = Array.isArray(siblings) ? siblings.length : 0
  const isFirst = rowTarget == null || rowTarget.index === 0
  const isLast = rowTarget == null || rowTarget.index >= count - 1

  // A rail declared WITHOUT `field:` is not in a list: it hosts nothing but the
  // field editors of what it sits beside (the address of a standalone CTA).
  const isRowRail = Boolean(topField)

  // A def that still forces the rail into the flow keeps its old rendering:
  // in-flow means no room to unfold on demand, so it shows every button.
  const forcedInline = evaluatedAttrs.style?.position === 'static'
  const declared = props.placement ?? (forcedInline ? 'inline' : 'auto')

  // Stamp the host every render: reactive-json re-creates row elements as the
  // array is reordered, and a stale `data-builder-row-pos` would drop a dragged
  // row at the wrong index. `data-builder-rail-host` is the CSS hook (the rail
  // is out of the flow, so it needs its host positioned, and it only shows on
  // that host's hover/focus); the `row-*` markers are the DRAG contract and
  // exist only when the rail really sits in a list row.
  useLayoutEffect(() => {
    const row = railRef.current?.parentElement
    if (!row) return
    const placement = declared === 'auto' ? detectPlacement(row) : declared
    railRef.current.setAttribute('data-placement', placement)
    row.setAttribute('data-builder-rail-host', '')
    row.setAttribute('data-builder-row-placement', placement)
    if (isRowRail) row.setAttribute('data-builder-row-root', '')
    if (rowTarget) {
      row.setAttribute('data-builder-row-field', topField)
      row.setAttribute('data-builder-row-list', rowTarget.arrayPath)
      row.setAttribute('data-builder-row-pos', String(rowTarget.index))
    }
    return () => {
      row.removeAttribute('data-builder-rail-host')
      row.removeAttribute('data-builder-row-root')
      row.removeAttribute('data-builder-row-placement')
      row.removeAttribute('data-builder-row-field')
      row.removeAttribute('data-builder-row-list')
      row.removeAttribute('data-builder-row-pos')
    }
  })

  const rowMarkers = rowTarget
    ? {
        'data-builder-field': topField,
        'data-builder-row-path': rowTarget.arrayPath,
        'data-builder-row-index': rowTarget.index,
      }
    : {}

  const actionButton = (action, Icon, title) => {
    const t = target(action)
    return (
      <button
        type="button"
        className="builder-row-action"
        title={title}
        aria-label={title}
        disabled={t == null}
        data-action={action}
        data-builder-row-action={t ? action : undefined}
        data-builder-field={t ? topField : undefined}
        data-builder-row-path={t?.arrayPath ?? undefined}
        data-builder-row-index={t?.index ?? undefined}
        data-builder-options={action === 'insert' ? options : undefined}
        // Names the removed thing in the canvas's "Annuler" banner.
        data-builder-row-label={action === 'remove' ? item : undefined}
      >
        <Icon size={13} aria-hidden="true" />
      </button>
    )
  }

  // The grip is the drag source AND a keyboard reorder control. Dragging alone
  // would fail WCAG 2.5.7 (Dragging Movements): the up/down buttons next to it
  // are the single-pointer alternative, the arrow keys the keyboard one.
  const gripTitle = label('move', 'Déplacer')
  const grip = (
    <button
      type="button"
      className="builder-row-action builder-row-grip"
      draggable={rowTarget != null && count > 1}
      disabled={rowTarget == null}
      data-action="move"
      data-builder-row-grip=""
      {...rowMarkers}
      title={`${gripTitle} — glisser, ou flèches haut et bas`}
      aria-label={`${gripTitle} — glisser, ou flèches haut et bas`}
    >
      <GripVertical size={13} aria-hidden="true" />
    </button>
  )

  // Field editors hosted BY the rail (`edit:`), one button each. A `column:`
  // entry edits that cell of the current row (its `field:` is the rail's own);
  // an entry with its own `field:` edits a whole field — that is how a lone CTA
  // gets its address edited without a pill parked next to it.
  const editButtons = (Array.isArray(props.edit) ? props.edit : []).map((decl, i) => {
    if (decl == null || typeof decl !== 'object') return null
    if (decl.column != null) {
      // Outside any row of the field (the template rendered as the list itself)
      // a cell has no address: nothing to edit.
      if (!isRowRail || rowPath == null) return null
      return <RailEditButton key={i} decl={decl} fieldKey={topField} rowPath={`${rowPath}.${decl.column}`} />
    }
    const key = splitField(decl.field ?? '').topField
    if (!key) return null
    return <RailEditButton key={i} decl={decl} fieldKey={key} rowPath={null} />
  })

  return (
    <div
      role="toolbar"
      aria-label={isRowRail ? label('toolbar', 'Actions') : (props.labels?.toolbar ?? 'Modifier')}
      ref={railRef}
      {...restAttrs}
      className={['builder-edit-only builder-row-toolbar', className].filter(Boolean).join(' ')}
    >
      {/* No list, no reordering: an edit-only rail is just its buttons — and it
          must NOT fold itself away behind a grip that doesn't exist. */}
      {isRowRail ? (
        <>
          {grip}
          <span className="builder-row-more">
            {editButtons}
            {!isFirst && actionButton('up', ChevronUp, label('up', 'Monter'))}
            {!isLast && actionButton('down', ChevronDown, label('down', 'Descendre'))}
            {actionButton('insert', Plus, label('insert', 'Insérer après'))}
            {actionButton('remove', Trash2, label('remove', 'Supprimer'))}
          </span>
        </>
      ) : editButtons}
    </div>
  )
}
